import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { UbiIdleService } from 'uber-ui';

import { Domain } from 'modules/shared/models/domain.models';
import { Role, RoleType } from 'modules/shared/models/role.models';
import { LoginResponse, ForgotPasswordResponse } from 'modules/shared/models/auth.models';
import { CurrentUser } from 'modules/shared/models/account.models';

import { SnackbarService } from 'app/services/snackbar.service';
import { AuthService } from 'modules/core/services/auth.service';

import {
  LogIn, AuthActionTypes, LogInSuccess, LogInFailure, LoginChallengeRequired, ClearLogIn, ConfirmNinetyDaysPasswordSuccess,
  ConfirmNinetyDaysPasswordFailure, ResetPasswordSuccess, ResetPasswordFailure, ForgotPasswordSuccess, ForgotPasswordFailure,
  CompleteFirstLogInSuccess, CompleteFirstLogInFailure, ChangePasswordSuccess, ChangePasswordFailure, UpdateUserNameFailure,
  UpdateUserNameSuccess, GetUserSuccess, GetUserFailure, GetUser, LogOut, EnableMfaSuccess, EnableMfaFailure, MfaConfirmSuccess,
  MfaConfirmFailure, DisableMfaSuccess, DisableMfaFailure, MfaConfirmLoginSuccess, MfaConfirmLoginFailure, GetUserRolesSuccess,
  GetUserRolesFailure, ClearGetUser
} from '../actions/auth.actions';
import {
  LogoutDispatchedAction, ConfirmNinetyDaysPasswordDispatchedAction, ResetPasswordDispatchedAction, ForgotPasswordDispatchedAction,
  CompleteFirstLoginDispatchedAction, ChangePasswordDispatchedAction, UpdateUserNameDispatchedAction, GetUserDispatchedAction,
  MfaConfirmDispatchedAction, MfaConfirmLoginDispatchedAction, GetUserRolesDispatchedAction
} from 'store/models/auth.models';
import { ClearLoadList } from 'store/actions/domain.actions';
import { AppState, Selectors } from 'store/root-reducer';

@Injectable()
export class AuthEffects {

  constructor(private actions: Actions,
              private authService: AuthService,
              private router: Router,
              private store: Store<AppState>,
              private ubiIdleService: UbiIdleService,
              private dialog: MatDialog,
              private snackbar: SnackbarService) { }

  @Effect() Login: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.LOGIN),
    map((action: LogIn) => action.payload),
    switchMap(payload => {
      return this.authService.login(payload).pipe(
        switchMap((loginResponse: LoginResponse) => {
          if (loginResponse.user && !loginResponse.challenge) {
            return this.authService
              .checkDbExistence(loginResponse.user.username)
              .pipe(
                map(() => loginResponse),
                catchError((err: any) => {
                  return [throwError({ message: err && err.error && err.error.detail ? err.error.detail : err.message }),
                          new LogOut({ isSessionTimeOut: false })];
                })
              );
          } else {
            return of(loginResponse);
          }
        }),
        map((loginResponse: LoginResponse) => {
          if (loginResponse.challenge) {
            return new LoginChallengeRequired({
              challenge: loginResponse.challenge,
              user: loginResponse.user
            });
          }
          return new LogInSuccess(loginResponse);
        }),
        catchError((error: any) => {
          this.snackbar.error(`Error ${error.message}`);
          return of(new LogInFailure(error));
        })
      );
    }),
  );

  @Effect({ dispatch: false }) LogInSuccess: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.LOGIN_SUCCESS),
    withLatestFrom(this.store.select(Selectors.domain.getCurrentDomain)),
    tap(([payload, domain]: [any, Domain]) => {
      this.ubiIdleService.setBrowserLogin();
      this.ubiIdleService.setAuthState('signedIn');
      this.snackbar.success('Logged in successfully');
      if (!domain) {
        this.router.navigateByUrl('/select-domain');
      } else {
        this.router.navigateByUrl('/dashboard');
      }
    })
  );

  @Effect() GetUser: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.GET_USER),
    switchMap((input: GetUserDispatchedAction) => {
      const bypassCache = input.payload ? input.payload.bypassCache : false;
      return this.authService
        .getUser(bypassCache)
        .pipe(
          switchMap((user: any) => {
            return of(new GetUserSuccess(user));
          }),
          catchError((error: any) => {
            this.router.navigateByUrl('login');
            return [new GetUserFailure(error), new LogOut({ isSessionTimeOut: false })];
          })
        );
    })
  );

  @Effect() ConfirmNinetyDaysPassword: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.CONFIRM_NINETY_DAYS_PASSWORD),
    switchMap((input: ConfirmNinetyDaysPasswordDispatchedAction) => {
      return this.authService
        .confirmNinetyDaysChangePassword(input.payload.user)
        .pipe(
          switchMap((confirmNinetyDaysPasswordResponse: any) => {
            this.snackbar.success('Please reset your password to continue to access the system');
            return of(new ConfirmNinetyDaysPasswordSuccess(confirmNinetyDaysPasswordResponse));
          }),
          catchError((error: any) => {
            this.snackbar.error(`Error ${error.message}`);
            return of(new ConfirmNinetyDaysPasswordFailure(error));
          })
        );
    })
  );

  @Effect() CompleteFirstLogin: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.COMPLETE_FIRST_LOGIN),
    switchMap((input: CompleteFirstLoginDispatchedAction) => {
      return this.authService
        .completeFirstLogin(input.payload.user, input.payload.newPassword)
        .pipe(
          switchMap((completeFirstLoginResponse: any) => {
            this.router.navigateByUrl('dashboard');
            this.snackbar.success('Complete login successfully');
            return of(new CompleteFirstLogInSuccess(completeFirstLoginResponse));
          }),
          catchError((error: any) => {
            this.snackbar.error(`Error ${error.message}`);
            return of(new CompleteFirstLogInFailure(error));
          })
        );
    })
  );

  @Effect() ResetPassword: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.RESET_PASSWORD),
    switchMap((input: ResetPasswordDispatchedAction) => {
      return this.authService
        .resetPassword(input.payload.username, input.payload.code, input.payload.newPassword)
        .pipe(
          switchMap((response: boolean) => {
            this.router.navigateByUrl('login');
            this.snackbar.success('Password successfully reset');
            return of(new ResetPasswordSuccess(response));
          }),
          catchError((error: any) => {
            this.snackbar.error(`Error ${error.message}`);
            return of(new ResetPasswordFailure(error));
          })
        );
    })
  );

  @Effect() ForgotPassword: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.FORGOT_PASSWORD),
    switchMap((input: ForgotPasswordDispatchedAction) => {
      return this.authService
        .forgotPassword(input.payload.email)
        .pipe(
          switchMap((forgotResponse: ForgotPasswordResponse) => {
            this.snackbar.success(`An email with a verification code has been sent to ${forgotResponse.CodeDeliveryDetails.Destination}!`);
            return of(new ForgotPasswordSuccess(forgotResponse));
          }),
          catchError((error: any) => {
            this.snackbar.error(`Error ${error.message}`);
            return of(new ForgotPasswordFailure(error));
          })
        );
    })
  );

  @Effect() ChangePassword: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.CHANGE_PASSWORD),
    switchMap((input: ChangePasswordDispatchedAction) => {
      return this.authService
        .changePassword(input.payload.oldPassword, input.payload.newPassword)
        .pipe(
          switchMap((response: boolean) => {
            this.snackbar.success('Password successfully updated');
            return [new ChangePasswordSuccess(response), new GetUser({ bypassCache: true })];
          }),
          catchError((error: any) => {
            this.snackbar.error(`Error ${error.message}`);
            return of(new ChangePasswordFailure(error));
          })
        );
    })
  );

  @Effect() UpdateUserName: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.UPDATE_USER_NAME),
    switchMap((input: UpdateUserNameDispatchedAction) => {
      return forkJoin([
        this.authService.updateUserName(input.payload.name),
        this.authService.updateUserNameDB(input.payload.oauthID, input.payload.name)
      ]).pipe(
        switchMap(([updateUserNameResponse, updateUserNameDBResponse]) => {
          this.snackbar.success('User name successfully updated');
          return [new UpdateUserNameSuccess(updateUserNameResponse || updateUserNameDBResponse), new GetUser({ bypassCache: true })];
        }),
        catchError((error: any) => {
          this.snackbar.error('Server Error while updateing user name!');
          return of(new UpdateUserNameFailure(error));
        })
      );
    })
  );

  @Effect() EnableMfa: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.ENABLE_MFA),
    switchMap(() => {
      return this.authService
        .enableMFA()
        .pipe(
          switchMap((secretCode: string) => {
            return of(new EnableMfaSuccess(secretCode));
          }),
          catchError((error: any) => {
            return of(new EnableMfaFailure(error));
          })
        );
    })
  );

  @Effect() ConfirmMfa: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.MFA_CONFIRM),
    switchMap((input: MfaConfirmDispatchedAction) => {
      return this.authService
        .enableMFAConfirm(input.payload.code)
        .pipe(
          switchMap((response: boolean) => {
            this.snackbar.success('Mfa successfully enabled');
            return [new MfaConfirmSuccess(response), new GetUser({ bypassCache: true })];
          }),
          catchError((error: any) => {
            this.snackbar.success(`Error ${error.message}`);
            return of(new MfaConfirmFailure(error));
          })
        );
    })
  );

  @Effect() ConfirmMfaLogin: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.MFA_CONFIRM_LOGIN),
    switchMap((input: MfaConfirmLoginDispatchedAction) => {
      return this.authService
        .twoFAConfirm(input.payload.username, input.payload.code)
        .pipe(
          switchMap((response: any) => {
            if (response.challenge) {
              return of(new LoginChallengeRequired({
                challenge: response.challenge,
                user: response.user
              }));
            }
            this.snackbar.success('Mfa successfully confirmed');
            return [new MfaConfirmLoginSuccess(response), new LogInSuccess(response), new GetUser({ bypassCache: true })];
          }),
          catchError((error: any) => {
            this.snackbar.success(`Error ${error.message}`);
            return of(new MfaConfirmLoginFailure(error));
          })
        );
    })
  );

  @Effect() DisableMfa: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.DISABLE_MFA),
    switchMap(() => {
      return this.authService
        .disableMFA()
        .pipe(
          switchMap((response: boolean) => {
            this.snackbar.success('Mfa successfully disabled');
            return [new DisableMfaSuccess(response), new GetUser({ bypassCache: true })];
          }),
          catchError((error: any) => {
            this.snackbar.success(`Error ${error.message}`);
            return of(new DisableMfaFailure(error));
          })
        );
    })
  );

  @Effect() LogOut: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.LOGOUT),
    switchMap((input: LogoutDispatchedAction) => {
      return this.authService
        .logOut()
        .pipe(
          switchMap(() => {
            if (input.payload.isSessionTimeOut) {
              this.snackbar.success('You have been logged out due to inactivity. Please log in again to continue');
            } else {
              this.snackbar.success('Logged out successfully');
            }
            this.dialog.closeAll();
            this.router.navigateByUrl('login');
            this.ubiIdleService.setBrowserLogout();
            this.ubiIdleService.setAuthState('signedOut');
            return [new ClearLogIn(), new ClearLoadList()];
          })
        );
    })
  );

  @Effect() GetUserRoles: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.GET_USER_ROLES),
    switchMap((input: GetUserRolesDispatchedAction) => {
      return this.authService.getUser(true)
        .pipe(
          switchMap((user: CurrentUser) => {
            return this.authService.getRoles(user.sub, input.payload.domainId)
              .pipe(
                tap((roles: Array<Role>) => {
                  if (this.router.url !== '/admin/user-permissions') {
                    const isOnlyAdmin: boolean = !roles.some((role: Role) => role.name === RoleType.L1) &&
                                                !roles.some((role: Role) => role.name === RoleType.L2) &&
                                                !roles.some((role: Role) => role.name === RoleType.L3);
                    if (isOnlyAdmin) {
                      this.router.navigateByUrl(input.payload.currentPath ? input.payload.currentPath : 'admin');
                    } else {
                      this.router.navigateByUrl(input.payload.currentPath ? input.payload.currentPath : 'dashboard');
                    }
                  } else {
                    if (!roles.some((userRole: Role) => userRole.name === 'ADMIN')) {
                      this.router.navigateByUrl(input.payload.currentPath ? input.payload.currentPath : 'dashboard');
                    }
                  }
                }),
                map((userRoles: Array<Role>) => {
                  return new GetUserRolesSuccess(userRoles);
                }),
                catchError((error: any) => {
                  return of(new GetUserRolesFailure(error));
                })
              );
          }),
          catchError(() => {
            return of(new LogOut({ isSessionTimeOut: false }));
          })
        );
    })
  );

  @Effect() LogInRequired: Observable<any> = this.actions.pipe(
    ofType(AuthActionTypes.LOGIN_REQUIRED),
    switchMap(() => {
      this.router.navigateByUrl('login');
      this.authService.logOut().subscribe();
      this.ubiIdleService.setBrowserLogout();
      this.ubiIdleService.setAuthState('signedOut');
      return [new ClearLoadList(), new ClearGetUser()];
    })
  );
}
